/**
 * Leviathan Web - Drivers Page
 * Quasar Prime LLC
 * Driver Management Interface
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { GeometricPanel } from '@components/ui/GeometricPanel';
import { API_URL } from '@/config/api';

interface Driver {
  id: string;
  driverNumber: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  licenseNumber: string | null;
  licenseState: string | null;
  licenseClass: string | null;
  licenseExpiry: string | null;
  hazmatCertified: boolean;
  hazmatExpiry: string | null;
  tankerEndorsement: boolean;
  twicCard: boolean;
  twicExpiry: string | null;
  medicalCardExpiry: string | null;
  status: string;
  employmentStatus: string;
  currentTruckId: string | null;
  isActive: boolean;
}

interface Stats {
  total: number;
  available: number;
  onDuty: number;
  offDuty: number;
  vacation: number;
  sick: number;
  hazmatCertified: number;
  complianceAlerts: number;
}

interface StatCardProps {
  label: string;
  value: string | number;
  status?: 'operational' | 'warning' | 'critical';
}

/**
 * Compact stat card for driver metrics
 */
function StatCard({ label, value, status }: StatCardProps) {
  const statusColor =
    status === 'operational'
      ? 'bg-success-green'
      : status === 'warning'
        ? 'bg-warning-amber'
        : status === 'critical'
          ? 'bg-error-red'
          : 'bg-steel';

  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="bg-panel-dark border border-panel-border p-4 clip-path-octagon relative">
        {status && (
          <div className="absolute top-3 right-3">
            <div
              className={`w-2 h-2 ${statusColor} rounded-full animate-pulse`}
              style={{ boxShadow: '0 0 8px currentColor' }}
            />
          </div>
        )}
        <div className="text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
          {label}
        </div>
        <div
          className="text-2xl font-mono font-bold text-amber"
          style={{
            textShadow: '0 0 10px rgba(255, 149, 0, 0.6)',
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

/**
 * Get status color and style
 */
function getStatusStyle(status: string): string {
  switch (status) {
    case 'AVAILABLE':
      return 'text-success-green bg-success-green/10';
    case 'ON_DUTY':
      return 'text-amber bg-amber/10';
    case 'OFF_DUTY':
      return 'text-text-tertiary bg-text-tertiary/10';
    case 'VACATION':
      return 'text-steel bg-steel/10';
    case 'SICK':
      return 'text-warning-amber bg-warning-amber/10';
    case 'SUSPENDED':
      return 'text-error-red bg-error-red/10';
    default:
      return 'text-text-secondary bg-text-secondary/10';
  }
}

/**
 * Get status icon
 */
function getStatusIcon(status: string): string {
  switch (status) {
    case 'AVAILABLE':
      return 'RDY';
    case 'ON_DUTY':
      return 'DRV';
    case 'OFF_DUTY':
      return 'OFF';
    case 'VACATION':
      return 'VAC';
    case 'SICK':
      return 'SIC';
    case 'SUSPENDED':
      return 'SUS';
    default:
      return '???';
  }
}

/**
 * Format date for display
 */
function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Check if date is expiring within 30 days
 */
function isExpiringSoon(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const today = new Date();
  const thirtyDays = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
  return date <= thirtyDays;
}

/**
 * Check if date is already expired
 */
function isExpired(dateStr: string | null): boolean {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}

const DriversPage = () => {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchDrivers();
  }, [search, statusFilter]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/drivers/stats`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchDrivers = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);

      const response = await fetch(`${API_URL}/api/drivers?${params}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDrivers(data.drivers);
      }
    } catch (error) {
      console.error('Failed to fetch drivers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
  };

  return (
    <div className="space-y-6 relative">
      {/* Header with geometric frame */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Drivers
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">Driver Management</p>
            </div>
            <Link
              to="/drivers/new"
              className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-6 transition-all uppercase tracking-wider relative group"
              style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
            >
              <span className="relative z-10">+ New Driver</span>
              <div
                className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ mixBlendMode: 'overlay' }}
              />
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <StatCard label="Total Drivers" value={stats.total} />
          <StatCard label="Available" value={stats.available} status="operational" />
          <StatCard
            label="On Duty"
            value={stats.onDuty}
            status={stats.onDuty > 0 ? 'warning' : undefined}
          />
          <StatCard label="Off Duty" value={stats.offDuty} />
          <StatCard label="Vacation" value={stats.vacation} />
          <StatCard label="Sick" value={stats.sick} />
          <StatCard label="HAZMAT Cert" value={stats.hazmatCertified} />
          <StatCard
            label="Compliance"
            value={stats.complianceAlerts}
            status={stats.complianceAlerts > 0 ? 'critical' : 'operational'}
          />
        </div>
      )}

      {/* Filters Panel */}
      <GeometricPanel>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, ID, phone..."
              className="form-input"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
            >
              <option value="">All Statuses</option>
              <option value="AVAILABLE">Available</option>
              <option value="ON_DUTY">On Duty</option>
              <option value="OFF_DUTY">Off Duty</option>
              <option value="VACATION">Vacation</option>
              <option value="SICK">Sick</option>
              <option value="SUSPENDED">Suspended</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-2 px-4 transition-all uppercase text-xs tracking-wider border border-panel-border hover:border-steel"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </GeometricPanel>

      {/* Drivers Table */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border clip-path-octagon overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="text-text-tertiary font-mono animate-pulse">
                &gt; Loading drivers...
              </div>
            </div>
          ) : drivers.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-text-tertiary font-mono text-lg mb-2">
                &gt; No drivers found
              </div>
              <p className="text-text-tertiary font-mono text-sm">
                {search || statusFilter
                  ? 'Try adjusting your search filters'
                  : 'Add your first driver to get started'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background border-b border-panel-border">
                  <tr>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Driver
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Contact
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      License
                    </th>
                    <th className="text-center px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Certifications
                    </th>
                    <th className="text-center px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Status
                    </th>
                    <th className="text-center px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {drivers.map((driver) => (
                    <tr
                      key={driver.id}
                      className="border-b border-panel-border hover:bg-amber/5 transition-colors cursor-pointer"
                      onClick={() => navigate(`/drivers/${driver.id}`)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-steel/20 rounded-full flex items-center justify-center text-steel font-mono font-bold">
                            {driver.firstName[0]}
                            {driver.lastName[0]}
                          </div>
                          <div>
                            <div className="text-amber font-mono font-semibold">
                              {driver.firstName} {driver.lastName}
                            </div>
                            <div className="text-text-tertiary font-mono text-xs">
                              {driver.driverNumber}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          {driver.phone && (
                            <div className="text-text-secondary font-mono text-sm">
                              {driver.phone}
                            </div>
                          )}
                          {driver.email && (
                            <div className="text-text-tertiary font-mono text-xs">
                              {driver.email}
                            </div>
                          )}
                          {!driver.phone && !driver.email && (
                            <span className="text-text-tertiary font-mono text-sm">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-text-secondary font-mono text-sm">
                            {driver.licenseClass || 'N/A'}
                            {driver.licenseNumber
                              ? ` #${driver.licenseNumber.slice(-4)}`
                              : ''}
                          </div>
                          <div
                            className={`font-mono text-xs ${
                              isExpired(driver.licenseExpiry)
                                ? 'text-error-red'
                                : isExpiringSoon(driver.licenseExpiry)
                                  ? 'text-warning-amber'
                                  : 'text-text-tertiary'
                            }`}
                          >
                            Exp: {formatDate(driver.licenseExpiry)}
                            {isExpired(driver.licenseExpiry) && ' EXPIRED'}
                            {!isExpired(driver.licenseExpiry) &&
                              isExpiringSoon(driver.licenseExpiry) &&
                              ' !'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          {driver.hazmatCertified && (
                            <span
                              className={`text-xs px-2 py-1 rounded ${
                                isExpired(driver.hazmatExpiry)
                                  ? 'bg-error-red/20 text-error-red'
                                  : isExpiringSoon(driver.hazmatExpiry)
                                    ? 'bg-warning-amber/20 text-warning-amber'
                                    : 'bg-success-green/20 text-success-green'
                              }`}
                              title={`HAZMAT - Exp: ${formatDate(driver.hazmatExpiry)}`}
                            >
                              HAZ
                            </span>
                          )}
                          {driver.tankerEndorsement && (
                            <span
                              className="text-xs bg-steel/20 text-steel px-2 py-1 rounded"
                              title="Tanker Endorsement"
                            >
                              TNK
                            </span>
                          )}
                          {driver.twicCard && (
                            <span
                              className={`text-xs px-2 py-1 rounded ${
                                isExpired(driver.twicExpiry)
                                  ? 'bg-error-red/20 text-error-red'
                                  : isExpiringSoon(driver.twicExpiry)
                                    ? 'bg-warning-amber/20 text-warning-amber'
                                    : 'bg-steel/20 text-steel'
                              }`}
                              title={`TWIC - Exp: ${formatDate(driver.twicExpiry)}`}
                            >
                              TWIC
                            </span>
                          )}
                          {!driver.hazmatCertified &&
                            !driver.tankerEndorsement &&
                            !driver.twicCard && (
                              <span className="text-text-tertiary text-xs">-</span>
                            )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`${getStatusStyle(driver.status)} font-mono text-xs uppercase tracking-wider font-semibold px-2 py-1 rounded`}
                        >
                          {getStatusIcon(driver.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center space-x-2">
                          {driver.status === 'AVAILABLE' && (
                            <button
                              onClick={() => navigate(`/dispatch/new?driverId=${driver.id}`)}
                              className="text-amber hover:text-amber/80 font-mono text-sm"
                              title="Create Dispatch"
                            >
                              🚚
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/drivers/${driver.id}/edit`)}
                            className="text-text-secondary hover:text-amber font-mono text-sm"
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => navigate(`/drivers/${driver.id}`)}
                            className="text-steel hover:text-amber font-mono text-sm"
                            title="View"
                          >
                            👁️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriversPage;
